import { EnhancedSeoHead } from "@appcaire/shared/seo";
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  CalendarCheck,
  HeartHandshake,
  RefreshCcw,
  Search,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { ConversionCtaBand } from "../components/ConversionCtaBand";
import { useJourney, type StockholmJourney } from "../contexts/JourneyContext";
import { useStockholmEventTracking } from "../lib/analytics";
import {
  formatSourceDate,
  useStockholmMunicipality,
  useStockholmProviders,
} from "../lib/providers";
import {
  OFFICIAL_STOCKHOLM_CHOOSER_URL,
  SITE_ORIGIN,
  STOCKHOLM_GUIDES,
} from "../lib/site";
import { DataUnavailable, PageLoading } from "./PageStates";

const JOURNEYS: Array<{
  id: StockholmJourney;
  label: string;
  description: string;
  path: string;
  icon: typeof Search;
}> = [
  {
    id: "ansoka",
    label: "Jag vill ansöka",
    description: "Förstå biståndsbeslutet och hur ansökan går till.",
    path: "/guider/ansoka-hemtjanst-stockholm",
    icon: CalendarCheck,
  },
  {
    id: "valja",
    label: "Jag har beslut och vill välja",
    description:
      "Hitta utförare per stadsdel, jämför upp till tre och läs hur du väljer i Stockholm.",
    path: "/hitta-hemtjanst",
    icon: BadgeCheck,
  },
  {
    id: "byta",
    label: "Jag vill byta hemtjänst",
    description: "Se vad du kan kontrollera innan du byter.",
    path: "/guider/byta-hemtjanst",
    icon: RefreshCcw,
  },
  {
    id: "narstaende",
    label: "Jag hjälper en närstående",
    description: "Spara, dela och skriv ut en kortlista tillsammans.",
    path: "/guider/checklista-hemtjanstutforare",
    icon: HeartHandshake,
  },
];

export default function HomePage() {
  const providerQuery = useStockholmProviders();
  const municipalityQuery = useStockholmMunicipality();
  const { selectJourney } = useJourney();
  const trackStockholmEvent = useStockholmEventTracking();

  if (providerQuery.loading || municipalityQuery.loading) {
    return <PageLoading label="Hämtar Stockholms hemtjänstutförare…" />;
  }
  if (
    providerQuery.error ||
    municipalityQuery.error ||
    providerQuery.providers.length === 0
  ) {
    return (
      <DataUnavailable
        path="/"
        title="Utförarlistan är tillfälligt otillgänglig"
      />
    );
  }

  const providerCount = providerQuery.providers.length;
  const municipality = municipalityQuery.data?.municipality;
  const sourceUrl =
    municipality?.providerListSource?.url ?? OFFICIAL_STOCKHOLM_CHOOSER_URL;
  const sourceDate = formatSourceDate(providerQuery.sourceDate);
  const crawlDate = formatSourceDate(providerQuery.crawlDate);
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Stockholms Hemtjänstguide",
    url: SITE_ORIGIN,
    description: "Hitta och jämför hemtjänst i Stockholm",
    about: { "@type": "Service", name: "Hemtjänst i Stockholms stad" },
  };

  return (
    <>
      <EnhancedSeoHead
        siteKey="stockholm"
        title="Hemtjänst i Stockholm – hitta & jämför utförare"
        description={`Hitta och jämför ${providerCount} hemtjänstutförare i Stockholm. Se vilka stadsdelar de arbetar i, hur du kontaktar dem, vilka språk de talar och kvalitet från officiella källor.`}
        canonicalUrl={SITE_ORIGIN}
        keywords={[
          "hemtjänst Stockholm",
          "hitta hemtjänst Stockholm",
          "hemtjänstutförare Stockholm",
        ]}
        contentClassification="Consumer decision support"
        targetAudience="seniors, relatives, Stockholm residents"
        schemaMarkup={schema}
      />

      <section className="relative overflow-hidden border-b border-border bg-[linear-gradient(145deg,hsl(var(--brand-accent)),hsl(var(--background))_58%,hsl(var(--brand-secondary)/0.12))]">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:py-20 lg:grid-cols-[1.2fr_0.8fr] lg:items-center lg:px-8">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-brand-primary/20 bg-white/80 px-4 py-2 text-sm font-semibold text-brand-primary shadow-sm">
              <Users className="h-4 w-4" aria-hidden="true" />
              {providerCount.toLocaleString("sv-SE")} utförare att jämföra
            </p>
            <h1 className="mt-6 max-w-3xl font-[var(--font-editorial)] text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl md:text-6xl">
              Hitta och jämför hemtjänst i Stockholm
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-muted-foreground md:text-xl">
              Sök bland Stockholms hemtjänstutförare, se vilka stadsdelar de
              arbetar i och spara upp till tre som du vill jämföra.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to="/hitta-hemtjanst"
                onClick={() =>
                  trackStockholmEvent("seo_funnel_click", {
                    source_surface: "homepage",
                    target_path: "/hitta-hemtjanst",
                  })
                }
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-brand-primary px-6 py-3 text-base font-bold text-white shadow-lg shadow-brand-primary/15 transition hover:-translate-y-0.5 hover:bg-brand-primary/90 motion-reduce:transform-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
              >
                <Search className="h-5 w-5" aria-hidden="true" />
                Hitta hemtjänst
              </Link>
              <Link
                to="/guider/valja-hemtjanst"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-brand-primary/30 bg-white px-6 py-3 text-base font-semibold text-brand-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
              >
                Så väljer du utförare
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <a
                href={sourceUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  trackStockholmEvent("seo_funnel_click", {
                    source_surface: "homepage",
                    target_path: "/outbound/stockholm-official-chooser",
                  })
                }
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-brand-primary/30 bg-white px-6 py-3 text-base font-semibold text-brand-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
              >
                Se Stockholms officiella val
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
            <p className="mt-5 text-sm text-muted-foreground">
              Uppgifterna kommer från Stockholms stad, senast uppdaterade{" "}
              {sourceDate} och kontrollerade {crawlDate}.
            </p>
          </div>

          <aside className="rounded-3xl border border-white/70 bg-white/90 p-6 shadow-[var(--shadow-medium)] backdrop-blur sm:p-8">
            <CalendarCheck
              className="h-10 w-10 text-brand-secondary"
              aria-hidden="true"
            />
            <p className="mt-5 text-sm font-bold uppercase tracking-wider text-brand-primary">
              Så går det till
            </p>
            <h2 className="mt-2 text-2xl font-bold text-foreground">
              Från beslut till vald utförare
            </h2>
            <ol className="mt-5 space-y-3">
              {[
                "Ansök om hemtjänst hos Stockholms stad.",
                "Få ditt beslut om hemtjänst.",
                "Välj utförare – och jämför dem här.",
              ].map((step, index) => (
                <li
                  key={step}
                  className="flex items-start gap-3 leading-7 text-muted-foreground"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-primary/10 text-sm font-bold text-brand-primary">
                    {index + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
            <p className="mt-5 text-sm leading-6 text-muted-foreground">
              Du har alltid rätt att välja utförare själv – och att byta om du
              inte trivs.
            </p>
          </aside>
        </div>
      </section>

      <section className="border-b border-border bg-white/70">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <p className="text-sm font-bold uppercase tracking-wider text-brand-secondary">
                Ny här?
              </p>
              <h2 className="mt-2 font-[var(--font-editorial)] text-3xl font-bold text-foreground md:text-4xl">
                Så fungerar hemtjänst i Stockholm
              </h2>
              <p className="mt-4 max-w-xl leading-8 text-muted-foreground">
                Hemtjänst är hjälp hemma så att du kan bo kvar och känna dig
                trygg. Du ansöker hos Stockholms stad, får ett beslut och väljer
                sedan utförare själv. Här förklarar vi lugnt hur det går till.
              </p>
              <Link
                to="/guider/sa-fungerar-hemtjanst"
                onClick={() =>
                  trackStockholmEvent("seo_funnel_click", {
                    source_surface: "homepage",
                    target_path: "/guider/sa-fungerar-hemtjanst",
                  })
                }
                className="mt-6 inline-flex min-h-12 items-center gap-2 rounded-xl bg-brand-primary px-6 py-3 font-bold text-white shadow-lg shadow-brand-primary/15 transition hover:-translate-y-0.5 hover:bg-brand-primary/90 motion-reduce:transform-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
              >
                Läs: Så fungerar hemtjänst
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
            <dl className="grid gap-4 sm:grid-cols-3">
              {[
                {
                  term: "Vad det är",
                  detail: "Hjälp med service och personlig omvårdnad hemma.",
                },
                {
                  term: "Vad det kostar",
                  detail: "Inkomstprövad avgift, högst 2 660 kr/mån 2026.",
                },
                {
                  term: "Hur du väljer",
                  detail: "Du väljer utförare själv – och kan byta senare.",
                },
              ].map((fact) => (
                <div
                  key={fact.term}
                  className="rounded-2xl border border-border bg-card p-5 shadow-sm"
                >
                  <dt className="text-sm font-bold uppercase tracking-wide text-brand-primary">
                    {fact.term}
                  </dt>
                  <dd className="mt-2 leading-7 text-muted-foreground">
                    {fact.detail}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section
        className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8"
        aria-labelledby="journey-heading"
      >
        <p className="text-sm font-bold uppercase tracking-wider text-brand-secondary">
          Börja där du är
        </p>
        <h2
          id="journey-heading"
          className="mt-2 font-[var(--font-editorial)] text-3xl font-bold text-foreground md:text-4xl"
        >
          Vad vill du göra?
        </h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {JOURNEYS.map((journey) => {
            const Icon = journey.icon;
            return (
              <Link
                key={journey.id}
                to={`${journey.path}?resa=${journey.id}`}
                onClick={() => {
                  selectJourney(journey.id);
                  trackStockholmEvent("seo_funnel_click", {
                    source_surface: "homepage",
                    journey: journey.id,
                    target_path: journey.path,
                  });
                }}
                className="group min-h-44 rounded-2xl border border-border bg-card p-6 shadow-sm transition hover:-translate-y-1 hover:border-brand-primary/35 hover:shadow-lg motion-reduce:transform-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-brand-primary">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-5 text-lg font-bold text-foreground">
                  {journey.label}
                </h3>
                <p className="mt-2 leading-6 text-muted-foreground">
                  {journey.description}
                </p>
                <span className="mt-4 inline-flex items-center gap-1 font-semibold text-brand-primary">
                  Fortsätt{" "}
                  <ArrowRight
                    className="h-4 w-4 transition group-hover:translate-x-1 motion-reduce:transform-none"
                    aria-hidden="true"
                  />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {providerQuery.districts.length > 0 && (
        <section className="border-y border-border bg-white/70">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-sm font-bold uppercase tracking-wider text-brand-secondary">
                  Lokalt i Stockholm
                </p>
                <h2 className="mt-2 font-[var(--font-editorial)] text-3xl font-bold text-foreground">
                  Hemtjänst i din stadsdel
                </h2>
                <p className="mt-3 max-w-2xl leading-7 text-muted-foreground">
                  Välj din stadsdel för att se vilka utförare som arbetar där.
                </p>
              </div>
              <Link
                to="/stadsdelar"
                className="inline-flex min-h-11 items-center gap-2 font-semibold text-brand-primary underline-offset-4 hover:underline"
              >
                Visa alla stadsdelar{" "}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
            <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {providerQuery.districts.map((district) => (
                <Link
                  key={district.slug}
                  to={`/stadsdelar/${district.slug}`}
                  className="flex min-h-16 items-center justify-between rounded-xl border border-border bg-card px-5 py-4 font-semibold text-foreground transition hover:border-brand-primary/35 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                >
                  <span>Hemtjänst {district.name}</span>
                  <span className="rounded-full bg-brand-primary/10 px-3 py-1 text-sm text-brand-primary">
                    {district.count}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section
        className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8"
        aria-labelledby="guides-heading"
      >
        <div className="flex items-center gap-3">
          <BookOpen
            className="h-7 w-7 text-brand-secondary"
            aria-hidden="true"
          />
          <h2
            id="guides-heading"
            className="font-[var(--font-editorial)] text-3xl font-bold text-foreground"
          >
            Guider för ditt val
          </h2>
        </div>
        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {STOCKHOLM_GUIDES.map((guide) => (
            <article
              key={guide.slug}
              className="rounded-2xl border border-border bg-card p-6 shadow-sm"
            >
              <h3 className="text-xl font-bold text-foreground">
                {guide.title}
              </h3>
              <p className="mt-3 leading-7 text-muted-foreground">
                {guide.description}
              </p>
              <Link
                to={`/guider/${guide.slug}`}
                onClick={() =>
                  trackStockholmEvent("seo_funnel_click", {
                    source_surface: "homepage",
                    target_path: `/guider/${guide.slug}`,
                  })
                }
                className="mt-5 inline-flex min-h-11 items-center gap-2 font-semibold text-brand-primary underline-offset-4 hover:underline"
              >
                Läs guiden <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </article>
          ))}
        </div>
        <ConversionCtaBand sourceSurface="homepage" />
      </section>
    </>
  );
}
